import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  listBuildings,
  listGroups,
  type Building,
  type BuildingGroup,
} from "@/lib/api/building";
import { getSession } from "@/lib/auth";
import {
  decodeInventoryScope,
  encodeInventoryScope,
  searchEqual,
  DEFAULT_INVENTORY_FILTER,
  type AssetType,
  type Status,
  type InventoryFilter,
} from "./inventory-scope-codec";

export type { AssetType, Status, InventoryFilter };

/** One `<select>`-ready entry in the place facet: "Kaikki paikkakunnat" (`id: "all"`) followed
 *  by every place actually present in the loaded building set, labeled with its count. */
export type CityFilterOption = { id: string; label: string };

type Ctx = {
  filter: InventoryFilter;
  setCity: (c: string) => void;
  setType: (t: AssetType) => void;
  setStatus: (s: Status) => void;
  groupId: string | null;
  setGroupId: (id: string | null) => void;
  // Single-building scope (ADR-014). When set, perspectives render this one building.
  // Exclusive with attribute filters / group — choosing a filter/group exits building scope,
  // and choosing a building clears filters + group.
  buildingId: string | null;
  setBuildingId: (id: string | null) => void;
  /** Resolved from the shared inventory cache — prefer this over a second useBuildings(). */
  selectedBuilding: Building | null;
  groups: BuildingGroup[];
  activeGroup: BuildingGroup | null;
  reset: () => void;
  /** Re-fetch buildings + groups (e.g. after Hallitse create). */
  refreshInventory: () => void;
  isFiltered: boolean;
  countLabel: string;
  /** Place facet options derived from the currently loaded building set (`liveCityCounts`
   *  below), sorted deterministically. The single source for every place-filter `<select>` —
   *  see `InventoryFilters.tsx` and `BuildingManagerOverlay.tsx`. */
  cityFilterOptions: CityFilterOption[];
  /** Shared inventory rows — the map and list should not each call `useBuildings()`. */
  buildings: Building[];
  /** False until the first `GET /buildings` settles (success or empty). */
  inventoryReady: boolean;
};

const FilterCtx = createContext<Ctx | null>(null);
// Search params are declared once on the root route (they're inherited by every child route),
// so reach for them by route id rather than importing the `Route` object — that would be a
// circular import, since __root.tsx itself imports this provider.
const rootRouteApi = getRouteApi("__root__");

export function InventoryFilterProvider({ children }: { children: ReactNode }) {
  // useNavigate() (not rootRouteApi.useNavigate): search-only updates from the root navigate
  // resolve to `/` and kick the user off Analyysi/etc. `to: "."` keeps the current path.
  const navigate = useNavigate();
  const search = rootRouteApi.useSearch();

  // Seeded once from the URL at mount (lazy initializer, runs once) — the sync effect below is
  // the write path from then on, so this never re-reads `search` on later renders.
  const [filter, setFilter] = useState<InventoryFilter>(
    () => decodeInventoryScope(search).filter,
  );
  const [groupId, setGroupIdState] = useState<string | null>(
    () => decodeInventoryScope(search).groupId,
  );
  const [buildingId, setBuildingIdState] = useState<string | null>(
    () => decodeInventoryScope(search).buildingId,
  );
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [totalBuildings, setTotalBuildings] = useState<number | null>(null);
  const [liveCityCounts, setLiveCityCounts] = useState<Record<string, number>>(
    {},
  );
  const [groups, setGroups] = useState<BuildingGroup[]>([]);
  const [inventoryTick, setInventoryTick] = useState(0);

  useEffect(() => {
    function fetchData() {
      if (!getSession()) return;
      listBuildings().then(
        (rows) => {
          const counts: Record<string, number> = {};
          for (const b of rows) {
            counts[b.city] = (counts[b.city] ?? 0) + 1;
          }
          setBuildings(rows);
          setTotalBuildings(rows.length);
          setLiveCityCounts(counts);
          // Unauthorized/unknown building id in a shared URL → clear silently.
          setBuildingIdState((id) =>
            id && !rows.some((b) => b.id === id) ? null : id,
          );
        },
        () => {},
      );
      listGroups()
        .then((fetchedGroups) => {
          setGroups(fetchedGroups);
          // Unauthorized/unknown group id in a shared URL → clear silently.
          setGroupIdState((id) =>
            id && !fetchedGroups.some((g) => g.id === id) ? null : id,
          );
        })
        .catch(() => {});
    }
    fetchData();
    window.addEventListener("kairos:login", fetchData);
    return () => window.removeEventListener("kairos:login", fetchData);
  }, [inventoryTick]);

  // Single source of truth: URL search is canonical, this context is the derived runtime view.
  // Every writer above only touches local state; this effect is the one place that writes through.
  // `search`/`navigate` are intentionally excluded from deps: reacting to `search` here would
  // refire on every route's own navigation, not just scope changes.
  useEffect(() => {
    const next = encodeInventoryScope({ filter, groupId, buildingId });
    if (searchEqual(next, search)) return;
    void navigate({ to: ".", search: () => next, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, groupId, buildingId]);

  // Read-back path: the URL can change without going through the writer above — browser
  // Back/Forward, or a link opened while this provider is already mounted. Resync state from
  // it. `filter`/`groupId`/`buildingId` are intentionally excluded from deps: they're only read
  // to detect whether this `search` already matches current state (i.e. it was our own write
  // above), not to retrigger on every state change — that direction is the writer's job.
  useEffect(() => {
    const next = encodeInventoryScope({ filter, groupId, buildingId });
    if (searchEqual(next, search)) return;
    const decoded = decodeInventoryScope(search);
    setFilter(decoded.filter);
    setGroupIdState(decoded.groupId);
    setBuildingIdState(decoded.buildingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const value = useMemo<Ctx>(() => {
    const activeGroup = groupId
      ? (groups.find((g) => g.id === groupId) ?? null)
      : null;
    const selectedBuilding = buildingId
      ? (buildings.find((b) => b.id === buildingId) ?? null)
      : null;
    const isFiltered =
      filter.city !== "all" ||
      filter.type !== "all" ||
      filter.status !== "all" ||
      groupId !== null ||
      buildingId !== null;
    const total = totalBuildings ?? 0;
    const cityFilterOptions: CityFilterOption[] = [
      { id: "all", label: "Kaikki paikkakunnat" },
      ...Object.entries(liveCityCounts)
        .sort(([a], [b]) => a.localeCompare(b, "fi"))
        .map(([city, cityCount]) => ({
          id: city,
          label: `${city} (${cityCount})`,
        })),
    ];

    let count = activeGroup ? activeGroup.building_ids.length : total;
    if (filter.city !== "all") count = liveCityCounts[filter.city] ?? 0;
    if (filter.type !== "all")
      count = Math.max(filter.city !== "all" ? 0 : 1, Math.round(count * 0.35));
    if (filter.status !== "all") count = Math.max(0, Math.round(count * 0.4));

    const countLabel =
      totalBuildings === null
        ? "Ladataan…"
        : selectedBuilding
          ? `${selectedBuilding.name} · 1 kohde`
          : buildingId
            ? "1 kohde"
            : activeGroup
              ? `${activeGroup.name} · ${activeGroup.building_ids.length} kiinteistöä`
              : isFiltered
                ? `${count} / ${total} kohdetta`
                : `${total} kohdetta`;

    return {
      filter,
      setCity: (city) => {
        setBuildingIdState(null);
        setFilter((f) => ({ ...f, city }));
      },
      setType: (type) => {
        setBuildingIdState(null);
        setFilter((f) => ({ ...f, type }));
      },
      setStatus: (status) => {
        setBuildingIdState(null);
        setFilter((f) => ({ ...f, status }));
      },
      groupId,
      setGroupId: (id) => {
        setBuildingIdState(null);
        setGroupIdState(id);
      },
      buildingId,
      setBuildingId: (id) => {
        if (id !== null) {
          setGroupIdState(null);
          setFilter(DEFAULT_INVENTORY_FILTER);
        }
        setBuildingIdState(id);
        // Newly created buildings may not be in the shared cache yet.
        if (id !== null && !buildings.some((b) => b.id === id)) {
          setInventoryTick((t) => t + 1);
        }
      },
      selectedBuilding,
      groups,
      activeGroup,
      reset: () => {
        setFilter(DEFAULT_INVENTORY_FILTER);
        setGroupIdState(null);
        setBuildingIdState(null);
      },
      refreshInventory: () => setInventoryTick((t) => t + 1),
      isFiltered,
      countLabel,
      cityFilterOptions,
      buildings,
      inventoryReady: totalBuildings !== null,
    };
  }, [
    filter,
    groupId,
    buildingId,
    buildings,
    groups,
    totalBuildings,
    liveCityCounts,
  ]);

  return <FilterCtx.Provider value={value}>{children}</FilterCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInventoryFilter() {
  const ctx = useContext(FilterCtx);
  if (!ctx)
    throw new Error(
      "useInventoryFilter must be used within InventoryFilterProvider",
    );
  return ctx;
}
